import axios from 'axios';

import { authService } from './auth';
import Constants from '../app.constants';
import { errors } from './errors';
import { session } from './session';

class ApiService {
  constructor() {
    this.Constants = Constants;
    this.Errors = errors;
    this.Session = session;

    this.http = axios.create({
      baseURL: this.Constants.HOST,
    });

    this.clientId = this.Constants.CLIENT_ID;
    this.clientSecret = this.Constants.CLIENT_SECRET;

    this.noReloadPaths = [
      '/v2/login',
      '/v2/reset-password-request',
      '/v2/reset-password-completion',
      '/v2/create-user',
      '/v2/change-password',
      '/v2/reset-password',
      '/v2/reset-password-completion',
      '/v2/privacy-policy',
      '/v2/merchant/account/change-password',
      '/v2/publisher/account/change-password',
      '/v2/publisher/terms-conditions',
    ];

    // Binding methods necessary here to keep context of 'this' in inceptor class
    this.http.interceptors.request.use(
      this.requestInterceptor.bind(this),
      this.requestErrorHandler.bind(this),
    );
    this.http.interceptors.response.use(
      resp => this.responseInterceptor(resp),
      resp => this.responseErrorHandler(resp),
    );
  }

  // Interceptor functions
  requestInterceptor(config) {
    const parser = document.createElement('a');
    parser.href = `${config.baseURL}${config.url}`;

    // Grabs hostname (host:port) instead of just host when using a proxy
    const host =
      parser.port !== '80' && parser.port !== '443' ? `//${parser.host}` : `//${parser.hostname}`;
    const narrativHost = this.Constants.HOST;
    // Provide authorization header to any request to Narrativ API
    config.headers = config.headers || {};

    return this.checkForRefresh(config).then(() => {
      if (this.Session.accessToken && host === narrativHost.match(/\/\/.*/)[0]) {
        config.headers.Authorization = `Bearer ${this.Session.accessToken}`;
      }
      return config;
    });
  }

  responseInterceptor(response) {
    return response || Promise.resolve(response);
  }

  requestErrorHandler(error) {
    return error || Promise.reject(error);
  }

  responseErrorHandler(errorResp) {
    const resp = errorResp.response;
    let error = resp;
    const noReloadPaths = this.noReloadPaths.concat(
      `/v2/publisher/${session.orgId}/account-settings/general-settings`,
    );

    if (!resp) {
      console.error('Network error'); // E.g. If backend was not up
    } else if ([401, 403].includes(resp.status)) {
      error = resp.data && resp.data.error;
      const code = error && error.code;
      if (this.Errors.codes[code] === 'forbiddenUpdate') {
        error = resp.data && resp.data.error;
      } else if (
        this.Errors.codes[code] === 'authHeader' ||
        !noReloadPaths.includes(window.location.pathname)
      ) {
        console.error('Missing or expired access token', resp.data);
        return authService.logout(true);
      }
    } else if (resp.status === 400 && resp.data.error === 'invalid_grant') {
      console.error('Expired access token', resp.data);
      return this.logout();
    } else {
      error = resp.data && resp.data.error;
    }

    return Promise.reject(error);
  }

  logout() {
    /*
     * In cases where requests are made with an expired access token,
     * this happens before AuthService is loaded. So we use the
     * local logout() function instead.
     */
    delete window.localStorage.currentUser;

    return this.post('/api/v0/logout/').then(() => {
      window.location.assign('/v2/login');
    });
  }

  /* Helper functions */
  checkForRefresh(config) {
    if (
      window.location.pathname === '/v2/login' ||
      window.location.pathname === '/v2/reset-password-request' ||
      window.location.pathname === '/v2/reset-password-completion' ||
      window.location.pathname === '/v2/create-user' ||
      window.location.pathname === '/v2/publisher/terms-conditions' ||
      config.url === '/api/v0/token/' ||
      config.url === '/api/v0/logout/'
    ) {
      return Promise.resolve(false);
    }

    let { currentUser } = window.localStorage;
    currentUser = JSON.parse(currentUser);

    let refreshTokenPromise = false;

    if (currentUser) {
      // If access_token is expired or is about to expire, then refresh it
      const now = Date.now();
      const expirationMins = (currentUser.exp - now / 1000) / 60;
      if (expirationMins < 5) {
        refreshTokenPromise = this.refreshUserToken(currentUser);
      }
    }

    return Promise.resolve(refreshTokenPromise);
  }

  refreshUserToken(currentUser) {
    if (!this.refreshPromise) {
      const data = {
        client_id: this.clientId,
        client_secret: this.clientSecret,
        grant_type: 'refresh_token',
        refresh_token: currentUser.refreshToken,
      };
      this.refreshPromise = this.post('/api/v0/token/', data);
    }

    return this.refreshPromise
      .then(resp => {
        const data = resp.data.data[0];
        if (data) {
          this.Session.update({
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            exp: data.access_token_payload.exp,
          });
        }

        /*
         * Must set promise to null so that it will continue
         * refreshing access token in the future.
         */
        this.refreshPromise = null;
      })
      .catch(() => {
        authService.logout();
        this.refreshPromise = null;
      });
  }

  /* HTTP aliases */
  get(path, params, options) {
    let config = {};
    if (params) config.params = params;
    /* Example options object: {headers: {Range: 'bytes=0-0'}} */
    if (options) config = Object.assign(config, options);

    return this.http.get(path, config);
  }

  post(path, data) {
    return this.http.post(path, data);
  }

  put(path, data) {
    return this.http.put(path, data);
  }

  delete(path, data) {
    const dataObj = { data };
    return this.http.delete(path, dataObj);
  }
}

export const api = new ApiService();
